import { callAPI } from '../../network/networkConnection';

const API_URL = process.env.REACT_APP_APIUrl;

const appendDateFilters = (params, dateFilters = {}) => {
  if (dateFilters?.startDate) {
    params.append('startDate', dateFilters.startDate);
  }
  if (dateFilters?.endDate) {
    params.append('endDate', dateFilters.endDate);
  }
};

export const SearchLogService = {
  /**
   * Get comprehensive search log analytics
   * @param {string} timeRange - 'all', '24h', '7d', '30d'
   */
  getAnalytics: async (timeRange = 'all', dateFilters = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('timeRange', timeRange);
      appendDateFilters(params, dateFilters);
      const response = await callAPI(`${API_URL}admin/search-log/analytics?${params.toString()}`, 'GET', {
        'Content-Type': 'application/json',
      });
      return response;
    } catch (error) {
      console.error('Error fetching search log analytics:', error);
      throw error;
    }
  },

  getUserAnalytics: async (timeRange = 'all', dateFilters = {}, page = 1, limit = 20, sortBy = 'totalSearches') => {
    try {
      const params = new URLSearchParams();
      params.append('timeRange', timeRange);
      params.append('page', page);
      params.append('limit', limit);
      params.append('sortBy', sortBy);
      appendDateFilters(params, dateFilters);
      const response = await callAPI(`${API_URL}admin/search-log/user-analytics?${params.toString()}`, 'GET', {
        'Content-Type': 'application/json',
      });
      return response;
    } catch (error) {
      console.error('Error fetching user analytics:', error);
      throw error;
    }
  },

  getCategories: async (timeRange = 'all', dateFilters = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('timeRange', timeRange);
      appendDateFilters(params, dateFilters);
      const response = await callAPI(`${API_URL}admin/search-log/categories?${params.toString()}`, 'GET', {
        'Content-Type': 'application/json',
      });
      return response;
    } catch (error) {
      console.error('Error fetching category analytics:', error);
      throw error;
    }
  },

  getSecurity: async (timeRange = 'all', dateFilters = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('timeRange', timeRange);
      appendDateFilters(params, dateFilters);
      const response = await callAPI(`${API_URL}admin/search-log/security?${params.toString()}`, 'GET', {
        'Content-Type': 'application/json',
      });
      return response;
    } catch (error) {
      console.error('Error fetching security analytics:', error);
      throw error;
    }
  },

  /**
   * Get image search analytics
   * @param {string} timeRange - 'all', '24h', '7d', '30d'
   * @param {number} page - page number
   * @param {number} limit - items per page
   * @param {string} search - search by username or email
   */
  getImageSearchAnalytics: async (timeRange = 'all', page = 1, limit = 20, search = '', dateFilters = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('timeRange', timeRange);
      appendDateFilters(params, dateFilters);
      params.append('page', page);
      params.append('limit', limit);
      if (search) {
        params.append('search', search);
      }
      const response = await callAPI(`${API_URL}admin/search-log/image-search?${params.toString()}`, 'GET', {
        'Content-Type': 'application/json',
      });
      return response;
    } catch (error) {
      console.error('Error fetching image search analytics:', error);
      throw error;
    }
  },

  /**
   * Block or unblock a keyword
   */
  toggleKeywordBlock: async (keyword, isBlocked) => {
    try {
      const response = await callAPI(
        `${API_URL}admin/search-log/keyword/${encodeURIComponent(keyword)}/block`,
        'PUT',
        {
          'Content-Type': 'application/json',
        },
        JSON.stringify({ isBlocked })
      );
      return response;
    } catch (error) {
      console.error('Error toggling keyword block:', error);
      throw error;
    }
  },

  /**
   * Save search keyword (should NOT break search flow)
   */
  saveSearchKeyword: async (keyword, category = 'global', source = 'website') => {
    try {
      const response = await callAPI(
        `${API_URL}admin/utils/save-search-keyword`,
        'POST',
        {
          'Content-Type': 'application/json',
        },
        JSON.stringify({ keyword, category, source })
      );
      return response;
    } catch (error) {
      // Don't throw - just log, don't break search flow
      console.warn('Failed to save search keyword:', error);
      return { success: false, error };
    }
  },

  /**
   * Search by keyword, email, or IP
   * @param {string} query - keyword, email, or IP to search
   * @param {string} type - 'keyword', 'email', or 'ip'
   * @param {string} timeRange - 'all', '24h', '7d', '30d'
   */
  searchByKeywordOrEmail: async (query, type, timeRange = 'all', dateFilters = {}) => {
    try {
      const params = new URLSearchParams();
      params.append('query', query);
      params.append('type', type);
      params.append('timeRange', timeRange);
      appendDateFilters(params, dateFilters);
      const response = await callAPI(
        `${API_URL}admin/search-log/search?${params.toString()}`,
        'GET',
        {
          'Content-Type': 'application/json',
        }
      );
      return response;
    } catch (error) {
      console.error('Error searching by keyword, email, or IP:', error);
      throw error;
    }
  },
  

  // ==================== Product Intent Tracking ====================

  /**
   * Track PDP (Product Detail Page) open event
   * @param {string} productId - Product ID
   */
  trackPdpOpen: async (productId) => {
    try {
      const response = await callAPI(
        `${API_URL}admin/track/pdp-open`,
        'POST',
        {
          'Content-Type': 'application/json',
        },
        JSON.stringify({ productId })
      );
      return response;
    } catch (error) {
      // Don't throw - just log, don't break UI
      console.warn('Failed to track PDP open:', error);
      return { success: false, error };
    }
  },

  /**
   * Track Add to Cart event
   * @param {string} productId - Product ID
   * @param {number} quantity - Quantity added to cart
   */
  trackAddToCart: async (productId, quantity = 1) => {
    try {
      const response = await callAPI(
        `${API_URL}admin/track/add-to-cart`,
        'POST',
        {
          'Content-Type': 'application/json',
        },
        JSON.stringify({ productId, quantity })
      );
      return response;
    } catch (error) {
      // Don't throw - just log, don't break UI
      console.warn('Failed to track add to cart:', error);
      return { success: false, error };
    }
  },

  // ==================== Product Intent Analytics ====================

  /**
   * Get product intent summary analytics
   * @param {Object} params - { range, startDate, endDate }
   */
  getProductIntentSummary: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.range) queryParams.append('range', params.range);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const response = await callAPI(
        `${API_URL}admin/product-intent/summary?${queryParams.toString()}`,
        'GET',
        {
          'Content-Type': 'application/json',
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching product intent summary:', error);
      throw error;
    }
  },

  /**
   * Get user-wise product intent analytics
   * @param {Object} params - { range, startDate, endDate, page, limit }
   */
  getProductIntentUsers: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.range) queryParams.append('range', params.range);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      if (params.userType) queryParams.append('userType', params.userType);
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);

      const response = await callAPI(
        `${API_URL}admin/product-intent/users?${queryParams.toString()}`,
        'GET',
        {
          'Content-Type': 'application/json',
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching product intent users:', error);
      throw error;
    }
  },

  /**
   * Get user-specific product intent details (PDP opens or add to cart)
   * @param {Object} params - { userId, eventType, range, startDate, endDate }
   */
  getUserProductIntentDetails: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.userId) queryParams.append('userId', params.userId);
      if (params.eventType) queryParams.append('eventType', params.eventType);
      if (params.range) queryParams.append('range', params.range);
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);

      const response = await callAPI(
        `${API_URL}admin/product-intent/user-details?${queryParams.toString()}`,
        'GET',
        {
          'Content-Type': 'application/json',
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching user product intent details:', error);
      throw error;
    }
  },
  

  /**
   * Get user's current cart products
   * @param {string} userId - User ID
   */
  getUserCart: async (userId) => {
    try {
      const response = await callAPI(
        `${API_URL}admin/user/${userId}`,
        'GET',
        {
          'Content-Type': 'application/json',
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching user cart:', error);
      throw error;
    }
  },

  /**
   * Get user's search keywords
   * @param {string} identifier - User email or IP address
   */
  getUserSearchKeywords: async (identifier) => {
    try {
      // Determine if identifier is email or IP
      const isEmail = identifier && identifier.includes('@') && !identifier.match(/^\d+\.\d+\.\d+\.\d+$/);
      const searchType = isEmail ? 'email' : 'ip';
      const response = await callAPI(
        `${API_URL}admin/search-log/search?query=${encodeURIComponent(identifier)}&type=${searchType}`,
        'GET',
        {
          'Content-Type': 'application/json',
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching user search keywords:', error);
      throw error;
    }
  },

  /**
   * Get all keywords with pagination and search
   * @param {Object} params - { page, limit, search, sortBy, sortOrder }
   */
  getAllKeywords: async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
      if (params.userType) queryParams.append('userType', params.userType);
      if (params.timeRange) queryParams.append('timeRange', params.timeRange);
      appendDateFilters(queryParams, params);

      const response = await callAPI(
        `${API_URL}admin/search-log/all-keywords?${queryParams.toString()}`,
        'GET',
        {
          'Content-Type': 'application/json',
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching all keywords:', error);
      throw error;
    }
  },

  /**
   * Get user list for a specific keyword
   * @param {string} keyword - The keyword to get users for
   */
  getKeywordUsers: async (keyword) => {
    try {
      const response = await callAPI(
        `${API_URL}admin/search-log/keyword-users?keyword=${encodeURIComponent(keyword)}`,
        'GET',
        {
          'Content-Type': 'application/json',
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching keyword users:', error);
      throw error;
    }
  },

  /**
   * Get search times for a specific keyword
   * @param {string} keyword - The keyword to get search times for
   */
  getKeywordSearchTimes: async (keyword) => {
    try {
      const response = await callAPI(
        `${API_URL}admin/search-log/keyword-search-times?keyword=${encodeURIComponent(keyword)}`,
        'GET',
        {
          'Content-Type': 'application/json',
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching keyword search times:', error);
      throw error;
    }
  },
  
  /**
   * Get all products for a specific image ID
   * @param {string} imageId - The image ID to search for
   * @param {number} page - page number
   * @param {number} limit - items per page
   */
  getProductsByImageId: async (imageId, page = 1, limit = 20) => {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', page);
      queryParams.append('limit', limit);

      const response = await callAPI(
        `${API_URL}admin/search-log/products-by-image/${encodeURIComponent(imageId)}?${queryParams.toString()}`,
        'GET',
        {
          'Content-Type': 'application/json',
        }
      );
      return response;
    } catch (error) {
      console.error('Error fetching products by image ID:', error);
      throw error;
    }
  },
  
  /**
   * Assign keywords to all products with a specific image ID
   * @param {string} imageId - The image ID
   * @param {Array<string>} keywords - Array of keywords to assign
   */
  assignKeywordsToProducts: async (imageId, keywords) => {
    try {
      const response = await callAPI(
        `${API_URL}admin/search-log/assign-keywords`,
        'POST',
        {
          'Content-Type': 'application/json',
        },
        JSON.stringify({ imageId, keywords })
      );
      return response;
    } catch (error) {
      console.error('Error assigning keywords:', error);
      throw error;
    }
  },
  
};
